import { AfterViewInit, Component, ContentChildren, ElementRef, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChildren } from '@angular/core';

import { TemplateUuids } from '../../../utils/shared-utils';
import { TabComponent } from './tab/tab.component';

@Component({
    selector: 'app-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.scss'],
    standalone: false
})
export class TabsComponent implements AfterViewInit, OnDestroy {
  @Input() tabIndex = 0;
  @Input() showSteps = false;
  @Input() ariaLabel = '';
  @Input() ariaLabelledBy = '';

  @Output() tabClick: EventEmitter<number> = new EventEmitter();

  @ViewChildren('tabElements') tabElements: QueryList<ElementRef>;
  @ContentChildren(TabComponent) tabs: QueryList<TabComponent>;

  templateUuids = new TemplateUuids();
  customTabs: any[] = [];

  private readonly keys: Record<string, string> = {
    Left: 'ArrowLeft',
    Up: 'ArrowUp',
    Right: 'ArrowRight',
    Down: 'ArrowDown',
    Space: ' ',
  };

  private readonly boundKeydownEventListener = this.keydownEventListener.bind(this);

  constructor(private elementRef: ElementRef) {}

  ngAfterViewInit() {
    this.customTabs = this.tabElements.map((tab) => tab.nativeElement);
    this.addListeners();
  }

  ngOnDestroy() {
    this.removeListeners();
  }

  public focusTabByIndex(index: number): void {
    if (this.customTabs?.[index]) {
      this.customTabs[index].focus();
    }
  }

  removeListeners() {
    for (const tab of this.customTabs) {
      tab.removeEventListener('keydown', this.boundKeydownEventListener);
    }
  }

  addListeners() {
    for (const tab of this.customTabs) {
      tab.addEventListener('keydown', this.boundKeydownEventListener);
    }
  }

  getElementId(name: string, id: number) {
    return this.templateUuids.get(name + String(id));
  }

  getAllTabIds() {
    return this.tabs
      .map((tabs, index) => {
        return this.getElementId('tab', index);
      })
      .join(' ');
  }

  keydownEventListener(event: KeyboardEvent) {
    const key = event.key;
    const target = event.target as HTMLElement;

    const currentIndex = Number(target.getAttribute('index'));
    let targetIndex = -1;

    if (key === this.keys.Space) {
      this.tabClicked(currentIndex);
      event.preventDefault();
    }

    if (key === this.keys.Left || key === this.keys.Up) {
      targetIndex = this.decrementTabIndex(currentIndex);
    } else if (key === this.keys.Right || key === this.keys.Down) {
      targetIndex = this.incrementTabIndex(currentIndex);
    }

    if (targetIndex >= 0 && currentIndex !== targetIndex) {
      this.focusTab(targetIndex);
      event.preventDefault();
    }
  }

  decrementTabIndex(index: number) {
    return this.getNextEnabledTabIndex(index, -1);
  }

  incrementTabIndex(index: number) {
    return this.getNextEnabledTabIndex(index, 1);
  }

  getNextEnabledTabIndex(currentIndex: number, step: 1 | -1): number {
    const tabs = this.tabs.toArray();

    if (!tabs.length) {
      return currentIndex;
    }

    let nextIndex = currentIndex;
    for (let attempts = 0; attempts < tabs.length; attempts++) {
      nextIndex = nextIndex + step;

      if (nextIndex < 0) {
        nextIndex = tabs.length - 1;
      } else if (nextIndex >= tabs.length) {
        nextIndex = 0;
      }

      if (!tabs[nextIndex].disabled) {
        return nextIndex;
      }
    }

    return currentIndex;
  }

  focusTab(index: number) {
    const selectedTab = this.customTabs[index];
    selectedTab.focus();
  }

  getActiveTabTemplate() {
    const activeTab = this.tabs.find(this.findActive.bind(this));
    return activeTab && activeTab.contentTemplate;
  }

  findActive(tab: any, index: number) {
    return this.isActive(index);
  }

  tabClicked(index: number) {
    const tab = this.tabs.toArray()[index];

    if (!tab.disabled) {
      this.tabClick.emit(index);
    }
  }

  isActive(index: number) {
    return index === this.tabIndex;
  }

  linkClickHandler(e: Event) {
    e.preventDefault();
  }
}
